import { Link } from 'react-router-dom'
import clsx from 'clsx'

import { siteBrandInitial, siteBrandMarkParts } from '@/constants/site-brand'
import { useSiteBrand } from '@/hooks'

type BrandSize = 'md' | 'lg'

const BRAND_SIZES: Record<
  BrandSize,
  { gap: string; logo: string; fallback: string; name: string }
> = {
  md: {
    gap: 'gap-2.5',
    logo: 'h-9 w-auto max-w-[150px]',
    fallback: 'h-9 w-9 rounded-lg text-xs',
    name: 'text-sm font-semibold tracking-wide',
  },
  lg: {
    gap: 'gap-3.5',
    logo: 'h-14 w-auto max-w-[300px] sm:h-16 sm:max-w-[340px]',
    fallback: 'h-12 w-12 rounded-xl text-base sm:h-14 sm:w-14 sm:text-lg',
    name: 'text-lg font-semibold tracking-tight sm:text-xl',
  },
}

type SiteBrandMarkProps = {
  className?: string
  nameClassName?: string
  size?: BrandSize
  to?: string
  /** 对齐 studio-web portal-brand */
  variant?: 'default' | 'portal'
}

/** 顶栏品牌：可显示 logo、站点名，或二者同时显示 */
export function SiteBrandMark({
  className,
  nameClassName,
  size = 'md',
  to = '/',
  variant = 'default',
}: SiteBrandMarkProps) {
  const { siteName, brandName, site, tenantName } = useSiteBrand()
  const { logo, showLogo, showName } = siteBrandMarkParts(site, tenantName)
  const labelName = brandName || siteName

  if (variant === 'portal') {
    return (
      <Link to={to} className={clsx('portal-brand', className)} aria-label={labelName}>
        {showLogo && logo ? (
          <img src={logo} alt={showName ? '' : labelName} draggable={false} />
        ) : (
          <span className="portal-brand-mark">{siteBrandInitial(labelName)}</span>
        )}
        {showName ? (
          <span className={clsx('portal-brand-name', nameClassName)}>
            {brandName || siteName}
          </span>
        ) : null}
      </Link>
    )
  }

  const styles = BRAND_SIZES[size]

  return (
    <Link
      to={to}
      aria-label={labelName}
      className={clsx(
        'group flex shrink-0 items-center text-zinc-100 transition-opacity hover:opacity-95',
        (showLogo && showName) || !showLogo ? styles.gap : null,
        className,
      )}
    >
      {showLogo && logo ? (
        <img
          src={logo}
          alt={showName ? '' : labelName}
          className={clsx(styles.logo, 'object-contain object-left drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)]')}
          draggable={false}
        />
      ) : (
        <span
          className={clsx(
            styles.fallback,
            'grid shrink-0 place-items-center bg-gradient-to-br from-indigo-500 to-violet-600 font-bold text-white shadow-[0_4px_16px_rgba(99,102,241,0.35)]',
          )}
        >
          {siteBrandInitial(labelName)}
        </span>
      )}
      {showName ? (
        <span className={clsx(styles.name, 'text-zinc-50', nameClassName)}>
          {brandName || siteName}
        </span>
      ) : null}
    </Link>
  )
}
